import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { getAnalyticsOverview } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    PieChart,
    Pie,
    Cell,
    Legend,
    ComposedChart,
    Line,
} from 'recharts';

// ─── Formatters ───────────────────────────────────────────────────────────────

const formatSEK = (value) =>
    new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: 'SEK',
        maximumFractionDigits: 0,
    }).format(value || 0);

const formatHours = (value) => `${Number(value || 0).toFixed(1)}h`;

const getLocalizedMonthNames = (locale) =>
    Array.from({ length: 12 }, (_, i) =>
        new Intl.DateTimeFormat(locale, { month: 'short' }).format(new Date(2000, i, 1))
    );

const PIE_COLORS = ['#7c3aed', '#db2777', '#ea580c'];

// ─── Custom tooltips ──────────────────────────────────────────────────────────

const HoursTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-background border border-border p-2 text-xs shadow-md">
            <p className="font-semibold mb-1">{label}</p>
            {payload.map((entry) => (
                <p key={entry.dataKey} style={{ color: entry.color }}>
                    {entry.name}: {formatHours(entry.value)}
                </p>
            ))}
        </div>
    );
};

const SEKTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-background border border-border p-2 text-xs shadow-md">
            <p className="font-semibold mb-1">{label}</p>
            {payload.map((entry) => (
                <p key={entry.dataKey} style={{ color: entry.color }}>
                    {entry.name}: {formatSEK(entry.value)}
                </p>
            ))}
        </div>
    );
};

const TrendTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-background border border-border p-2 text-xs shadow-md">
            <p className="font-semibold mb-1">{label}</p>
            {payload.map((entry) => (
                <p key={entry.dataKey} style={{ color: entry.color }}>
                    {entry.name}:{' '}
                    {entry.dataKey === 'cost_sek' ? formatSEK(entry.value) : formatHours(entry.value)}
                </p>
            ))}
        </div>
    );
};

const CostAllocationTooltip = ({ active, payload, label, tProjectAllocated, tFunctionFallback, tHeadcount, tTotal }) => {
    if (!active || !payload?.length) return null;
    const byKey = Object.fromEntries(payload.map((p) => [p.dataKey, p.value]));
    const projected = byKey.project_allocated_cost || 0;
    const fallback = byKey.main_function_fallback_cost || 0;
    const total = projected + fallback;
    const headcount = payload[0]?.payload?.headcount ?? 0;
    return (
        <div className="bg-background border border-border p-2 text-xs shadow-md min-w-[180px]">
            <p className="font-semibold mb-1">{label}</p>
            <p style={{ color: '#7c3aed' }}>{tProjectAllocated}: {formatSEK(projected)}</p>
            <p style={{ color: '#0369a1' }}>{tFunctionFallback}: {formatSEK(fallback)}</p>
            <p className="text-muted-foreground mt-1">{tTotal}: {formatSEK(total)}</p>
            <p className="text-muted-foreground">{tHeadcount}: {headcount}</p>
        </div>
    );
};

const PieTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const { name, value } = payload[0];
    return (
        <div className="bg-background border border-border p-2 text-xs shadow-md">
            <p className="font-semibold">{name}</p>
            <p>{formatSEK(value)}</p>
        </div>
    );
};

// ─── Budget status chip ───────────────────────────────────────────────────────

function BudgetStatusChip({ pct }) {
    if (pct >= 100) {
        return (
            <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-700 border border-red-300">
                ≥100%
            </span>
        );
    }
    if (pct >= 81) {
        return (
            <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 border border-amber-300">
                {pct}%
            </span>
        );
    }
    return (
        <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-green-100 text-green-700 border border-green-300">
            {pct}%
        </span>
    );
}

// ─── Summary card ─────────────────────────────────────────────────────────────

function SummaryCard({ title, value, sub }) {
    return (
        <Card className="border border-border shadow-none">
            <CardContent className="p-5">
                <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">{title}</p>
                <p className="font-heading text-2xl font-bold tracking-tight">{value}</p>
                {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
            </CardContent>
        </Card>
    );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({ message }) {
    return (
        <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
            {message}
        </div>
    );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AnalyticsPage() {
    const { t, i18n } = useTranslation();
    // useAuth is imported for role checks — the route guard already protects this
    // page, but we keep the import consistent with other admin pages.
    useAuth();

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    // ── filter state ──────────────────────────────────────────────────────────
    const [year, setYear] = useState(currentYear);
    const [monthFrom, setMonthFrom] = useState(1);
    const [monthTo, setMonthTo] = useState(currentMonth);
    const [projectFilter, setProjectFilter] = useState('all');
    const [employeeFilter, setEmployeeFilter] = useState('all');

    // pending (uncommitted) filter values — committed on Apply
    const [pendingYear, setPendingYear] = useState(currentYear);
    const [pendingMonthFrom, setPendingMonthFrom] = useState(1);
    const [pendingMonthTo, setPendingMonthTo] = useState(currentMonth);
    const [pendingProject, setPendingProject] = useState('all');
    const [pendingEmployee, setPendingEmployee] = useState('all');

    // ── data state ────────────────────────────────────────────────────────────
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);

    // project / employee option lists (populated from first unfiltered load)
    const [projectOptions, setProjectOptions] = useState([]);
    const [employeeOptions, setEmployeeOptions] = useState([]);

    // ── fetch ─────────────────────────────────────────────────────────────────
    const fetchData = useCallback(async (params) => {
        setLoading(true);
        try {
            const res = await getAnalyticsOverview({
                year: params.year,
                month_from: params.monthFrom,
                month_to: params.monthTo,
                ...(params.projectFilter !== 'all' ? { project_id: params.projectFilter } : {}),
                ...(params.employeeFilter !== 'all' ? { user_id: params.employeeFilter } : {}),
            });
            const d = res.data;
            setData(d);

            // Populate filter dropdowns on the first (unfiltered) fetch.
            // Active-only in dropdowns (industry standard) — charts still show all data.
            if (params.projectFilter === 'all' && params.employeeFilter === 'all') {
                if (d.hours_by_project?.length) {
                    setProjectOptions(d.hours_by_project
                        .filter((p) => p.is_active !== false)
                        .map((p) => ({ id: p.project_id, name: p.project_name })));
                }
                if (d.hours_by_user?.length) {
                    setEmployeeOptions(d.hours_by_user
                        .filter((u) => u.is_active !== false)
                        .map((u) => ({ id: u.user_id, name: u.user_name })));
                }
            }
        } catch (err) {
            toast.error(t('analytics.failed_load'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    // Auto-fetch on mount with defaults
    useEffect(() => {
        fetchData({ year, monthFrom, monthTo, projectFilter, employeeFilter });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── filter handlers ───────────────────────────────────────────────────────
    const handleApply = () => {
        const nextFrom = pendingMonthFrom;
        const nextTo = pendingMonthTo < pendingMonthFrom ? pendingMonthFrom : pendingMonthTo;
        setYear(pendingYear);
        setMonthFrom(nextFrom);
        setMonthTo(nextTo);
        setProjectFilter(pendingProject);
        setEmployeeFilter(pendingEmployee);
        fetchData({
            year: pendingYear,
            monthFrom: nextFrom,
            monthTo: nextTo,
            projectFilter: pendingProject,
            employeeFilter: pendingEmployee,
        });
    };

    const handleReset = () => {
        setPendingYear(currentYear);
        setPendingMonthFrom(1);
        setPendingMonthTo(currentMonth);
        setPendingProject('all');
        setPendingEmployee('all');
        setYear(currentYear);
        setMonthFrom(1);
        setMonthTo(currentMonth);
        setProjectFilter('all');
        setEmployeeFilter('all');
        fetchData({
            year: currentYear,
            monthFrom: 1,
            monthTo: currentMonth,
            projectFilter: 'all',
            employeeFilter: 'all',
        });
    };

    // ── derived data ──────────────────────────────────────────────────────────
    const summary = data?.summary || {};
    const hoursByProject = data?.hours_by_project || [];
    const hoursByMonth = data?.hours_by_month || [];
    const hoursByUser = data?.hours_by_user || [];
    const expensesByMonth = data?.expenses_by_month || [];
    const expensesByType = data?.expenses_by_type || [];
    const costByMainFunction = data?.cost_by_main_function || [];

    // ── supplier invoices (Leverantörsfakturor) ────────────────────────────
    const supplierByCostCode = data?.supplier_invoices_by_cost_code || [];
    const supplierBySupplier = data?.supplier_invoices_by_supplier || [];
    const supplierByMonth = data?.supplier_invoices_by_month || [];
    const totalSupplierInvoices = Number(summary.total_supplier_invoices_sek || 0);
    // Each invoice lands in exactly one cost-code bucket, so summing those
    // counts yields the total invoice count for the period.
    const supplierInvoiceCount = supplierByCostCode.reduce(
        (sum, r) => sum + Number(r.count || 0),
        0
    );
    // Supplier invoices have no project/user dimension; when either filter is
    // active the backend returns empty sections — surface an explanatory hint
    // rather than empty charts.
    const supplierFilterActive = projectFilter !== 'all' || employeeFilter !== 'all';
    const hasSupplierData = supplierByCostCode.length > 0 || totalSupplierInvoices > 0;

    const monthNames = getLocalizedMonthNames(i18n.language);

    // row.month from /api/analytics/overview is a "YYYY-MM" string, not a
    // number — parse the month digits the same way the Phase 20.9 supplier
    // chart below already does, with a guard against malformed values.
    const monthNameFromKey = (monthKey) => {
        const mNum = parseInt(String(monthKey ?? '').slice(5, 7), 10);
        return Number.isFinite(mNum) && mNum >= 1 && mNum <= 12
            ? monthNames[mNum - 1]
            : String(monthKey ?? '');
    };

    const monthlyTrendData = hoursByMonth.map((row) => ({
        name: monthNameFromKey(row.month),
        hours: Number(row.hours || 0),
        cost_sek: Number(row.cost_sek || 0),
    }));

    const monthlyExpenseData = expensesByMonth.map((row) => ({
        name: monthNameFromKey(row.month),
        mileage: Number(row.mileage_sek || 0),
        receipt: Number(row.receipt_sek || 0),
        allowance: Number(row.allowance_sek || 0),
    }));

    const pieData = expensesByType
        .filter((e) => Number(e.total_sek || 0) > 0)
        .map((e) => ({
            name: t(`analytics.expense_${e.type}`, { defaultValue: e.type }),
            value: Number(e.total_sek || 0),
        }));

    const budgetRows = hoursByProject.filter(
        (p) => p.budget_sek != null && Number(p.budget_sek) > 0
    );

    // Supplier-by-supplier can be long; cap to the top N (backend already sorts
    // desc) and flag the truncation with a visible subtitle.
    const SUPPLIER_TOP_N = 10;
    const supplierBySupplierTop = supplierBySupplier.slice(0, SUPPLIER_TOP_N);
    const supplierBySupplierTruncated = supplierBySupplier.length > SUPPLIER_TOP_N;

    const supplierMonthlyData = supplierByMonth.map((row) => ({
        name: monthNameFromKey(row.month),
        total: Number(row.total_sek || 0),
    }));

    const isAllEmpty =
        !loading &&
        hoursByProject.length === 0 &&
        hoursByMonth.length === 0 &&
        hoursByUser.length === 0 &&
        !hasSupplierData;

    // year dropdown: current year ± 2
    const yearOptions = [-2, -1, 0, 1, 2].map((d) => currentYear + d);

    const monthLabel = (m) =>
        new Date(2000, m - 1, 1).toLocaleString(i18n.language, { month: 'short' });

    // ── render ────────────────────────────────────────────────────────────────
    return (
        <div className="space-y-6" data-testid="analytics-page">
            {/* Header */}
            <div>
                <h1 className="font-heading text-4xl font-bold tracking-tight">{t('analytics.title')}</h1>
                <p className="text-muted-foreground mt-1">{t('analytics.subtitle')}</p>
            </div>

            {/* Filters */}
            <div className="border border-border bg-background p-4">
                <div className="flex flex-wrap items-end gap-3">
                    {/* Year */}
                    <div className="flex flex-col gap-1 min-w-[100px]">
                        <label className="text-xs text-muted-foreground uppercase tracking-widest">
                            {t('analytics.filter_year')}
                        </label>
                        <Select value={String(pendingYear)} onValueChange={(v) => setPendingYear(Number(v))}>
                            <SelectTrigger className="w-24" data-testid="filter-year">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {yearOptions.map((y) => (
                                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Month From */}
                    <div className="flex flex-col gap-1 min-w-[100px]">
                        <label className="text-xs text-muted-foreground uppercase tracking-widest">
                            {t('analytics.filter_month_from')}
                        </label>
                        <Select
                            value={String(pendingMonthFrom)}
                            onValueChange={(v) => {
                                const val = Number(v);
                                setPendingMonthFrom(val);
                                if (pendingMonthTo < val) setPendingMonthTo(val);
                            }}
                        >
                            <SelectTrigger className="w-28" data-testid="filter-month-from">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                                    <SelectItem key={m} value={String(m)}>{monthLabel(m)}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Month To */}
                    <div className="flex flex-col gap-1 min-w-[100px]">
                        <label className="text-xs text-muted-foreground uppercase tracking-widest">
                            {t('analytics.filter_month_to')}
                        </label>
                        <Select
                            value={String(pendingMonthTo)}
                            onValueChange={(v) => {
                                const val = Number(v);
                                setPendingMonthTo(val);
                                if (val < pendingMonthFrom) setPendingMonthFrom(val);
                            }}
                        >
                            <SelectTrigger className="w-28" data-testid="filter-month-to">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                                    <SelectItem key={m} value={String(m)}>{monthLabel(m)}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Project */}
                    <div className="flex flex-col gap-1 min-w-[140px]">
                        <label className="text-xs text-muted-foreground uppercase tracking-widest">
                            {t('analytics.filter_project')}
                        </label>
                        <Select value={pendingProject} onValueChange={setPendingProject}>
                            <SelectTrigger className="w-44" data-testid="filter-project">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t('analytics.filter_all')}</SelectItem>
                                {projectOptions.map((p) => (
                                    <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Employee */}
                    <div className="flex flex-col gap-1 min-w-[140px]">
                        <label className="text-xs text-muted-foreground uppercase tracking-widest">
                            {t('analytics.filter_employee')}
                        </label>
                        <Select value={pendingEmployee} onValueChange={setPendingEmployee}>
                            <SelectTrigger className="w-44" data-testid="filter-employee">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t('analytics.filter_all')}</SelectItem>
                                {employeeOptions.map((e) => (
                                    <SelectItem key={e.id} value={String(e.id)}>{e.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Buttons */}
                    <div className="flex items-end gap-2 ml-auto">
                        <Button
                            variant="outline"
                            className="uppercase tracking-wider text-xs"
                            onClick={handleReset}
                            data-testid="filter-reset"
                        >
                            {t('analytics.filter_reset')}
                        </Button>
                        <Button
                            className="uppercase tracking-wider text-xs"
                            onClick={handleApply}
                            disabled={loading}
                            data-testid="filter-apply"
                        >
                            {t('analytics.filter_apply')}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Loading spinner */}
            {loading && (
                <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-2 border-foreground border-t-transparent animate-spin" />
                </div>
            )}

            {/* Empty state */}
            {!loading && isAllEmpty && (
                <Card className="border border-border shadow-none">
                    <CardContent className="p-10 text-center text-muted-foreground">
                        {t('analytics.no_data')}
                    </CardContent>
                </Card>
            )}

            {/* Main content — only render when there is data */}
            {!loading && !isAllEmpty && (
                <>
                    {/* Summary cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                        <SummaryCard
                            title={t('analytics.summary_total_hours')}
                            value={formatHours(summary.total_hours)}
                        />
                        <SummaryCard
                            title={t('analytics.summary_labour_cost')}
                            value={formatSEK(summary.total_cost_sek)}
                        />
                        <SummaryCard
                            title={t('analytics.summary_expenses')}
                            value={formatSEK(summary.total_expenses_sek)}
                        />
                        <SummaryCard
                            title={t('analytics.summary_billable_split', {
                                billable: Number(summary.billable_hours || 0).toFixed(1),
                                internal: Number(summary.internal_hours || 0).toFixed(1),
                            })}
                            value=""
                            sub={t('analytics.summary_counts', { projects: summary.projects_count || 0, employees: summary.employees_count || 0 })}
                        />
                    </div>

                    {/* Charts — 2-column grid */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                        {/* Row 1 left: Hours by Project */}
                        <Card className="border border-border shadow-none">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-semibold uppercase tracking-widest">
                                    {t('analytics.chart_hours_by_project')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {hoursByProject.length === 0 ? (
                                    <EmptyState message={t('analytics.no_data')} />
                                ) : (
                                    <ResponsiveContainer width="100%" height={300}>
                                        <BarChart
                                            data={hoursByProject.map((p) => ({
                                                name: p.project_name,
                                                hours: Number(p.hours || 0),
                                            }))}
                                            layout="vertical"
                                            margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                            <XAxis
                                                type="number"
                                                tick={{ fontSize: 11 }}
                                                tickFormatter={(v) => `${v}h`}
                                            />
                                            <YAxis
                                                type="category"
                                                dataKey="name"
                                                tick={{ fontSize: 11 }}
                                                width={110}
                                            />
                                            <Tooltip content={<HoursTooltip />} />
                                            <Bar dataKey="hours" fill="#7c3aed" radius={[0, 3, 3, 0]} name={t('analytics.label_hours')} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </CardContent>
                        </Card>

                        {/* Row 1 right: Monthly Hours Trend */}
                        <Card className="border border-border shadow-none">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-semibold uppercase tracking-widest">
                                    {t('analytics.chart_monthly_trend')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {monthlyTrendData.length === 0 ? (
                                    <EmptyState message={t('analytics.no_data')} />
                                ) : (
                                    <ResponsiveContainer width="100%" height={300}>
                                        <ComposedChart
                                            data={monthlyTrendData}
                                            margin={{ top: 4, right: 24, left: 8, bottom: 4 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                                            <YAxis
                                                yAxisId="left"
                                                tick={{ fontSize: 11 }}
                                                tickFormatter={(v) => `${v}h`}
                                            />
                                            <YAxis
                                                yAxisId="right"
                                                orientation="right"
                                                tick={{ fontSize: 11 }}
                                                tickFormatter={(v) => formatSEK(v)}
                                                width={80}
                                            />
                                            <Tooltip content={<TrendTooltip />} />
                                            <Legend wrapperStyle={{ fontSize: 11 }} />
                                            <Bar
                                                yAxisId="left"
                                                dataKey="hours"
                                                fill="#7c3aed"
                                                radius={[3, 3, 0, 0]}
                                                name={t('analytics.label_hours')}
                                            />
                                            <Line
                                                yAxisId="right"
                                                type="monotone"
                                                dataKey="cost_sek"
                                                stroke="#db2777"
                                                strokeWidth={2}
                                                dot={false}
                                                name={t('analytics.label_cost_sek')}
                                            />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                )}
                            </CardContent>
                        </Card>

                        {/* Row 2 left: Expense Breakdown (Pie) */}
                        <Card className="border border-border shadow-none">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-semibold uppercase tracking-widest">
                                    {t('analytics.chart_expense_breakdown')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {pieData.length === 0 ? (
                                    <EmptyState message={t('analytics.no_data')} />
                                ) : (
                                    <ResponsiveContainer width="100%" height={300}>
                                        <PieChart>
                                            <Pie
                                                data={pieData}
                                                cx="50%"
                                                cy="50%"
                                                outerRadius={100}
                                                dataKey="value"
                                                label={({ name, percent }) =>
                                                    `${name} ${(percent * 100).toFixed(0)}%`
                                                }
                                                labelLine={true}
                                            >
                                                {pieData.map((_, index) => (
                                                    <Cell
                                                        key={`cell-${index}`}
                                                        fill={PIE_COLORS[index % PIE_COLORS.length]}
                                                    />
                                                ))}
                                            </Pie>
                                            <Tooltip content={<PieTooltip />} />
                                            <Legend wrapperStyle={{ fontSize: 11 }} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                )}
                            </CardContent>
                        </Card>

                        {/* Row 2 right: Hours by Employee */}
                        <Card className="border border-border shadow-none">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-semibold uppercase tracking-widest">
                                    {t('analytics.chart_hours_by_employee')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {hoursByUser.length === 0 ? (
                                    <EmptyState message={t('analytics.no_data')} />
                                ) : (
                                    <ResponsiveContainer width="100%" height={300}>
                                        <BarChart
                                            data={hoursByUser.map((u) => ({
                                                name: u.user_name,
                                                hours: Number(u.hours || 0),
                                            }))}
                                            layout="vertical"
                                            margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                            <XAxis
                                                type="number"
                                                tick={{ fontSize: 11 }}
                                                tickFormatter={(v) => `${v}h`}
                                            />
                                            <YAxis
                                                type="category"
                                                dataKey="name"
                                                tick={{ fontSize: 11 }}
                                                width={110}
                                            />
                                            <Tooltip content={<HoursTooltip />} />
                                            <Bar dataKey="hours" fill="#0369a1" radius={[0, 3, 3, 0]} name={t('analytics.label_hours')} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Row 3: Monthly Expenses stacked (full width) */}
                    <Card className="border border-border shadow-none">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-semibold uppercase tracking-widest">
                                {t('analytics.chart_monthly_expenses')}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {monthlyExpenseData.length === 0 ? (
                                <EmptyState message={t('analytics.no_data')} />
                            ) : (
                                <ResponsiveContainer width="100%" height={260}>
                                    <BarChart
                                        data={monthlyExpenseData}
                                        margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                                        <YAxis
                                            tick={{ fontSize: 11 }}
                                            tickFormatter={(v) => formatSEK(v)}
                                            width={80}
                                        />
                                        <Tooltip content={<SEKTooltip />} />
                                        <Legend wrapperStyle={{ fontSize: 11 }} />
                                        <Bar
                                            dataKey="mileage"
                                            stackId="expenses"
                                            fill="#7c3aed"
                                            radius={[0, 0, 0, 0]}
                                            name={t('analytics.expense_mileage')}
                                        />
                                        <Bar
                                            dataKey="receipt"
                                            stackId="expenses"
                                            fill="#db2777"
                                            radius={[0, 0, 0, 0]}
                                            name={t('analytics.expense_receipt')}
                                        />
                                        <Bar
                                            dataKey="allowance"
                                            stackId="expenses"
                                            fill="#ea580c"
                                            radius={[3, 3, 0, 0]}
                                            name={t('analytics.expense_allowance')}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </CardContent>
                    </Card>

                    {/* Cost by Main Function — full width stacked bar */}
                    <Card className="border border-border shadow-none">
                        <CardHeader className="pb-1">
                            <CardTitle className="text-sm font-semibold uppercase tracking-widest">
                                {t('analytics.chart_cost_by_main_function')}
                            </CardTitle>
                            <p className="text-xs text-muted-foreground mt-1">
                                {t('analytics.chart_cost_by_main_function_sub')}
                            </p>
                        </CardHeader>
                        <CardContent>
                            {costByMainFunction.length === 0 ? (
                                <EmptyState message={t('analytics.no_data')} />
                            ) : (
                                <ResponsiveContainer width="100%" height={280}>
                                    <BarChart
                                        data={costByMainFunction.map((row) => ({
                                            name: row.cost_code_name,
                                            project_allocated_cost: Number(row.project_allocated_cost || 0),
                                            main_function_fallback_cost: Number(row.main_function_fallback_cost || 0),
                                            headcount: row.headcount,
                                        }))}
                                        margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                                        <YAxis
                                            tick={{ fontSize: 11 }}
                                            tickFormatter={(v) => formatSEK(v)}
                                            width={88}
                                        />
                                        <Tooltip
                                            content={
                                                <CostAllocationTooltip
                                                    tProjectAllocated={t('analytics.label_project_allocated')}
                                                    tFunctionFallback={t('analytics.label_function_fallback')}
                                                    tHeadcount={t('analytics.tooltip_headcount')}
                                                    tTotal={t('analytics.tooltip_total_cost')}
                                                />
                                            }
                                        />
                                        <Legend wrapperStyle={{ fontSize: 11 }} />
                                        <Bar
                                            dataKey="project_allocated_cost"
                                            stackId="cost"
                                            fill="#7c3aed"
                                            radius={[0, 0, 0, 0]}
                                            name={t('analytics.label_project_allocated')}
                                        />
                                        <Bar
                                            dataKey="main_function_fallback_cost"
                                            stackId="cost"
                                            fill="#0369a1"
                                            radius={[3, 3, 0, 0]}
                                            name={t('analytics.label_function_fallback')}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </CardContent>
                    </Card>

                    {/* ── Supplier invoices (Leverantörsfakturor) ──────────────── */}
                    {supplierFilterActive ? (
                        <Card className="border border-border shadow-none">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-semibold uppercase tracking-widest">
                                    {t('analytics.section_supplier_invoices')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <EmptyState message={t('analytics.supplier_not_applicable')} />
                            </CardContent>
                        </Card>
                    ) : hasSupplierData ? (
                        <>
                            {/* Supplier invoices total */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                                <SummaryCard
                                    title={t('analytics.summary_supplier_invoices')}
                                    value={formatSEK(totalSupplierInvoices)}
                                    sub={t('analytics.summary_supplier_count', { count: supplierInvoiceCount })}
                                />
                            </div>

                            {/* By cost center + by supplier */}
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                                {/* Supplier invoices by cost center */}
                                <Card className="border border-border shadow-none">
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm font-semibold uppercase tracking-widest">
                                            {t('analytics.chart_supplier_by_cost_center')}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        {supplierByCostCode.length === 0 ? (
                                            <EmptyState message={t('analytics.no_data')} />
                                        ) : (
                                            <ResponsiveContainer width="100%" height={300}>
                                                <BarChart
                                                    data={supplierByCostCode.map((r) => ({
                                                        name: r.cost_code_name,
                                                        total: Number(r.total_sek || 0),
                                                    }))}
                                                    layout="vertical"
                                                    margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                                    <XAxis
                                                        type="number"
                                                        tick={{ fontSize: 11 }}
                                                        tickFormatter={(v) => formatSEK(v)}
                                                    />
                                                    <YAxis
                                                        type="category"
                                                        dataKey="name"
                                                        tick={{ fontSize: 11 }}
                                                        width={110}
                                                    />
                                                    <Tooltip content={<SEKTooltip />} />
                                                    <Bar
                                                        dataKey="total"
                                                        fill="#7c3aed"
                                                        radius={[0, 3, 3, 0]}
                                                        name={t('analytics.label_amount_sek')}
                                                    />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Supplier invoices by supplier (top N) */}
                                <Card className="border border-border shadow-none">
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm font-semibold uppercase tracking-widest">
                                            {t('analytics.chart_supplier_by_supplier')}
                                        </CardTitle>
                                        {supplierBySupplierTruncated && (
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {t('analytics.supplier_top_n', { n: SUPPLIER_TOP_N })}
                                            </p>
                                        )}
                                    </CardHeader>
                                    <CardContent>
                                        {supplierBySupplierTop.length === 0 ? (
                                            <EmptyState message={t('analytics.no_data')} />
                                        ) : (
                                            <ResponsiveContainer width="100%" height={300}>
                                                <BarChart
                                                    data={supplierBySupplierTop.map((r) => ({
                                                        name: r.supplier_name,
                                                        total: Number(r.total_sek || 0),
                                                    }))}
                                                    layout="vertical"
                                                    margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                                    <XAxis
                                                        type="number"
                                                        tick={{ fontSize: 11 }}
                                                        tickFormatter={(v) => formatSEK(v)}
                                                    />
                                                    <YAxis
                                                        type="category"
                                                        dataKey="name"
                                                        tick={{ fontSize: 11 }}
                                                        width={110}
                                                    />
                                                    <Tooltip content={<SEKTooltip />} />
                                                    <Bar
                                                        dataKey="total"
                                                        fill="#db2777"
                                                        radius={[0, 3, 3, 0]}
                                                        name={t('analytics.label_amount_sek')}
                                                    />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Supplier invoices by month (full width) */}
                            <Card className="border border-border shadow-none">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-semibold uppercase tracking-widest">
                                        {t('analytics.chart_supplier_by_month')}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {supplierMonthlyData.length === 0 ? (
                                        <EmptyState message={t('analytics.no_data')} />
                                    ) : (
                                        <ResponsiveContainer width="100%" height={260}>
                                            <BarChart
                                                data={supplierMonthlyData}
                                                margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                                                <YAxis
                                                    tick={{ fontSize: 11 }}
                                                    tickFormatter={(v) => formatSEK(v)}
                                                    width={80}
                                                />
                                                <Tooltip content={<SEKTooltip />} />
                                                <Bar
                                                    dataKey="total"
                                                    fill="#ea580c"
                                                    radius={[3, 3, 0, 0]}
                                                    name={t('analytics.label_amount_sek')}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    )}
                                </CardContent>
                            </Card>
                        </>
                    ) : null}

                    {/* Budget vs Actual table — only if any project has a budget */}
                    {budgetRows.length > 0 && (
                        <Card className="border border-border shadow-none">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-semibold uppercase tracking-widest">
                                    {t('analytics.table_budget_vs_actual')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                <div className="w-full overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-border">
                                                <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-widest text-muted-foreground">
                                                    {t('analytics.table_project')}
                                                </th>
                                                <th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-widest text-muted-foreground">
                                                    {t('analytics.table_budget')}
                                                </th>
                                                <th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-widest text-muted-foreground">
                                                    {t('analytics.table_actual_cost')}
                                                </th>
                                                <th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-widest text-muted-foreground">
                                                    {t('analytics.table_used_pct')}
                                                </th>
                                                <th className="text-center px-4 py-3 font-semibold text-xs uppercase tracking-widest text-muted-foreground">
                                                    {t('analytics.table_status')}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {budgetRows.map((p) => {
                                                const budget = Number(p.budget_sek);
                                                const actual = Number(p.cost_sek || 0);
                                                const pct = budget > 0 ? Math.round((actual / budget) * 100) : 0;
                                                return (
                                                    <tr key={p.project_id} className="border-b border-border/50 last:border-0 hover:bg-muted/40">
                                                        <td className="px-4 py-3 font-medium">{p.project_name}</td>
                                                        <td className="px-4 py-3 text-right font-mono text-muted-foreground">
                                                            {formatSEK(budget)}
                                                        </td>
                                                        <td className="px-4 py-3 text-right font-mono">
                                                            {formatSEK(actual)}
                                                        </td>
                                                        <td className="px-4 py-3 text-right font-mono">
                                                            {pct}%
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <BudgetStatusChip pct={pct} />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </>
            )}
        </div>
    );
}
